<template>
  <div>
    <PageHeader>
      <template #header-title>{{ $t('Placements') }}</template>
      <template #header-subheading>
        {{ $t('Roles or projects engaging your candidates') }}
      </template>
    </PageHeader>
    <GraphqlDataTable
      v-if="!defaultClientOwnerLoading"
      :filters="defaultFilters"
      :query="query"
      :additional-query-variables="additionalQueryVariables"
      :search-placeholder="$t('Search role, company, worker...')"
      :order-by="initialOrder"
      :filter-black-list="['accounts', 'supplierAccounts']"
      :max-height-adjustment="30"
    >
      <template #action="{ row }">
        <RecruiterPlacementActionColumn :row="row" />
      </template>
    </GraphqlDataTable>
  </div>
</template>

<script lang="ts" setup>
import { computed } from 'vue'
import { TypedQueryDocumentNode } from 'graphql/index'
import GraphqlDataTable from '@/components/GraphqlDataTable/GraphqlDataTable.vue'
import { graphql } from '@/gql'
import { HireOrderByColumn, SortOrder } from '@/gql/graphql'
import RecruiterPlacementActionColumn from '@/components/GraphqlDataTable/columns/RecruiterPlacement/RecruiterPlacementActionColumn.vue'
import PageHeader from '@/components/PageHeader.vue'
import { $t } from '@/app-bootstrapping/SetUpInternationalization'
import { atobSafe } from '@/components/GraphqlDataTable/filters/filter-tools'
import useDefaultClientOwnerFilter from '@/composables/useDefaultClientOwnerFilter'
import { user } from '@/composables/useUser'

const props = defineProps<{
  recruiterAccountId: string
  supplierAccountId?: string | null
  status?: string[]
}>()

const query = graphql(`
  query RecruiterPlacementsQuery(
    $accounts: [ID!]
    $supplierAccounts: [ID!]
    $search: String
    $page: Int!
    $first: Int!
    $orderBy: [HiresOrderByClauseInput!]
    $activeStatus: [HireActiveStatus!]
    $recruiterOwnershipIsExpired: Boolean
    $startDateRange: DateRangeInput
    $endDateRange: DateRangeInput
    $recruiterCompanies: [ID!]
    $endClients: [ID!]
    $clientOwners: [ID!]
    $recruiterWorkers: [ID!]
    $staffingAgencies: [ID!]
    $hasPaymentRequests: Boolean
    $missingPaymentRequestDateRange: DateRangeInput
  ) {
    hires(
      accounts: $accounts
      supplierAccounts: $supplierAccounts
      search: $search
      page: $page
      first: $first
      orderBy: $orderBy
      activeStatus: $activeStatus
      recruiterOwnershipIsExpired: $recruiterOwnershipIsExpired
      startDateRange: $startDateRange
      endDateRange: $endDateRange
      companies: $recruiterCompanies
      endClients: $endClients
      clientOwners: $clientOwners
      workers: $recruiterWorkers
      staffingAgencies: $staffingAgencies
      hasPaymentRequests: $hasPaymentRequests
      missingPaymentRequestDateRange: $missingPaymentRequestDateRange
    ) {
      data {
        ...HireActiveStatusColumn

        ...HireWorkerColumn

        ...HireTitleCompanyColumn

        startDate
        endDate

        ...HireTenureColumn

        ...RateColumnHire

        ...RecruiterPlacementTermsColumn
        ...RecruiterPlacementOwnershipColumn

        ...HirePaymentRequestColumn
        ...HireLastPaymentRequestColumn

        ...RecruiterPlacementContractColumn
        ...RecruiterPlacementActionColumn
      }
    }
  }
`) as TypedQueryDocumentNode

const { loading: defaultClientOwnerLoading, clientOwners } = useDefaultClientOwnerFilter()

// The table reads this once on mount, and URL state overrides it.
const defaultFilters = computed(() =>
  clientOwners.value.length
    ? { clientOwners: clientOwners.value.map((id) => ({ id, value: id, label: user.value.name })) }
    : {},
)

const initialOrder = {
  field: HireOrderByColumn.StartDate,
  order: SortOrder.Desc,
}

// A drill-through link may narrow the page's statuses, e.g. to a client's active placements.
const linkedStatuses = ((): string[] => {
  const raw = new URLSearchParams(window.location.search).get('activeStatus')
  try {
    return raw ? (JSON.parse(atobSafe(raw)) as { value: string }[]).map(({ value }) => value) : []
  } catch {
    return []
  }
})()

const additionalQueryVariables = computed(() => {
  const vars: Record<string, string[]> = {}
  if (props.supplierAccountId) {
    vars.supplierAccounts = [props.supplierAccountId]
    // Override GraphqlDataTable's default `accounts: [currentAccount]` —
    // on a supplier-managed hire that filters by *client* company.
    vars.accounts = []
  } else {
    vars.accounts = [props.recruiterAccountId]
    if (props.status?.length) {
      const linked = linkedStatuses.filter((status) => props.status?.includes(status))
      vars.activeStatus = linked.length ? linked : props.status
    }
  }
  return vars
})
</script>
